<template>
  <div class="roles-panel settings-panel-wrapper">
    <dod-view-title title="Uprawnienia użytkowników" :pink-title="true" />

    <div class="roles-table">
      <div class="roles-head">
        <span>UŻYTKOWNIK</span>
        <span>IMIĘ</span>
        <span>TELEFON</span>
        <span>ROLA</span>
        <span></span>
      </div>

      <div v-for="user in users" :key="user.phone" class="role-row">
        <span class="cell-user">{{ user.phone }}</span>
        <span class="cell-name">
          {{ `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-" }}
        </span>
        <span class="cell-phone">{{ user.adminPhone || "-" }}</span>
        <div class="cell-role">
          <custom-select
            :key="`role-${user.phone}-${renderKey}`"
            :name="`role-${user.phone}`"
            :options="roleOptions"
            :prePick="draftRoles[user.phone]"
            :graySelect="true"
            :large="true"
            @newPick="(val) => onChangeRole(user, val)"
          />
        </div>
        <div class="cell-action">
          <custom-button
            size="small"
            title="Zablokuj dostęp"
            :gradient="true"
            :disabled="blocking || saving"
            :customStyles="'width:170px;margin-top:0;'"
            @click="askBlock(user)"
          />
        </div>
      </div>

      <p v-if="users.length === 0" class="empty">Brak użytkowników.</p>
    </div>

    <div class="roles-footer">
      <custom-button
        size="large"
        title="Anuluj"
        :gray="true"
        :disabled="saving || blocking"
        @click="cancel"
      />
      <custom-button
        size="large"
        title="Zapisz"
        :disabled="saving || blocking || !isDirty"
        @click="save"
      />
    </div>

    <confirm-modal
      v-if="confirm"
      :message="confirm.message"
      confirm-label="Zablokuj dostęp"
      cancel-label="Anuluj"
      @confirm="onConfirmBlock"
      @cancel="confirm = null"
    />
  </div>
</template>

<script>
import DodViewTitle from "../DodViewTitle.vue";
import CustomButton from "../../CustomButton.vue";
import CustomSelect from "../../CustomSelect.vue";
import ConfirmModal from "./ConfirmModal.vue";

export default {
  name: "DodPanelRoles",
  components: { DodViewTitle, CustomButton, CustomSelect, ConfirmModal },
  data() {
    return {
      users: [],
      // Draft selection per user (phone -> role). Committed values live on `users`;
      // ANULUJ resets the draft, ZAPISZ persists only rows whose role actually changed.
      draftRoles: {},
      saving: false,
      blocking: false,
      confirm: null,
      // Bumped to re-mount CustomSelects (they hold their picked value internally).
      renderKey: 0,
      roleOptions: [
        {
          name: "Administrator (kierownik)",
          shortName: "Administrator (kierownik)",
          value: "ADMINISTRATOR",
        },
        { name: "Pracownik", shortName: "Pracownik", value: "PRACOWNIK" },
      ],
    };
  },
  computed: {
    isDirty() {
      return this.users.some(
        (u) => this.draftRoles[u.phone] !== u.role
      );
    },
  },
  async created() {
    await this.fetch();
  },
  methods: {
    async fetch() {
      try {
        this.users = await this.$store.dispatch("getPanelUserRoles");
      } catch (e) {
        this.users = [];
      }
      this.resetDraft();
    },
    resetDraft() {
      const draft = {};
      this.users.forEach((u) => {
        draft[u.phone] = u.role;
      });
      this.draftRoles = draft;
      this.renderKey++;
    },
    onChangeRole(user, value) {
      // Keys are pre-populated in resetDraft, so a plain assignment stays reactive.
      this.draftRoles[user.phone] = value;
    },
    cancel() {
      this.resetDraft();
      this.$emit("close");
    },
    async save() {
      if (this.saving || this.blocking) return;
      const changed = this.users.filter(
        (u) => this.draftRoles[u.phone] !== u.role
      );
      if (changed.length === 0) {
        this.$notify({ group: "success", text: "Brak zmian do zapisania" });
        return;
      }
      this.saving = true;
      try {
        for (const u of changed) {
          await this.$store.dispatch("setPanelUserRole", {
            adminPhone: u.phone,
            role: this.draftRoles[u.phone],
            adminName: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
          });
        }
        this.$notify({ group: "success", text: "Zapisano uprawnienia" });
        await this.fetch();
        this.$emit("close");
      } catch (e) {
        this.$notify({
          group: "error",
          text: "Nie udało się zapisać uprawnień",
        });
      } finally {
        this.saving = false;
      }
    },
    askBlock(user) {
      const name =
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.phone;
      this.confirm = {
        phone: user.phone,
        message:
          `Czy na pewno chcesz zablokować dostęp do panelu użytkownikowi <b>${name}</b>? ` +
          "Użytkownik straci dostęp do panelu i zniknie z tej listy. Dostęp można później " +
          "przywrócić w głównej aplikacji SeniorApp.",
      };
    },
    async onConfirmBlock() {
      const conf = this.confirm;
      this.confirm = null;
      if (!conf || this.blocking) return;
      this.blocking = true;
      try {
        await this.$store.dispatch("blockPanelUserAccess", {
          adminPhone: conf.phone,
        });
        this.$notify({ group: "success", text: "Zablokowano dostęp" });
        await this.fetch();
      } catch (e) {
        const text =
          e && e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : "Nie udało się zablokować dostępu";
        this.$notify({ group: "error", text });
      } finally {
        this.blocking = false;
      }
    },
  },
};
</script>

<style scoped>
.roles-panel {
  margin-top: 20px;
}
.roles-table {
  margin-top: 16px;
}
.roles-head,
.role-row {
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 1fr 2.2fr auto;
  align-items: center;
  column-gap: 16px;
}
.roles-head {
  padding: 0 4px 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #090741;
}
.role-row {
  padding: 16px 4px;
  border-bottom: 1px solid #eee;
}
.cell-user {
  font-weight: 600;
  font-size: 14px;
  color: #090741;
}
.cell-name,
.cell-phone {
  font-size: 14px;
  color: #090741;
}
.cell-action {
  display: flex;
  justify-content: flex-end;
}
.empty {
  color: #999;
  padding: 14px 4px;
}
.roles-footer {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 28px;
  padding-bottom: 8px;
}
</style>
